/**
 * Inspection Page
 *
 * Form for performing pre/post inspection of wasserij items.
 * Allows capturing dimensions, condition, notes, and photos.
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui';
import type { WasserijItem, InspectionData } from '../lib/salesforce';

// Mock data
const MOCK_ITEM: WasserijItem = {
  Id: 'wi-1',
  Name: 'WI-001',
  Werk_Order__c: 'wo-1',
  Product__r: { Name: 'Perzisch tapijt', ProductCode: 'WAS-PRIM-PERZISCH' },
  Lengte__c: 300,
  Breedte__c: 200,
  Oppervlakte_m__c: 6.0,
  Voorinspectie_gedaan__c: false,
};

type Condition = 'good' | 'fair' | 'poor';

export function InspectionPage() {
  const { workOrderId, itemId } = useParams();
  const navigate = useNavigate();

  const [condition, setCondition] = useState<Condition>('good');
  const [notes, setNotes] = useState('');
  const [length, setLength] = useState<string>(MOCK_ITEM.Lengte__c?.toString() || '');
  const [width, setWidth] = useState<string>(MOCK_ITEM.Breedte__c?.toString() || '');
  const [diameter, setDiameter] = useState<string>(MOCK_ITEM.Diameter__c?.toString() || '');
  const [isRound, setIsRound] = useState(!!MOCK_ITEM.Diameter__c);
  const [isSaving, setIsSaving] = useState(false);

  const item = MOCK_ITEM;

  const handleSave = async () => {
    setIsSaving(true);

    const inspectionData: InspectionData = {
      wasserijItemId: itemId!,
      condition,
      notes: notes || undefined,
      dimensions: isRound
        ? { diameter: diameter ? parseFloat(diameter) : undefined }
        : {
            length: length ? parseFloat(length) : undefined,
            width: width ? parseFloat(width) : undefined,
          },
      photos: [], // Would come from photo capture
    };

    // In real app, save to local DB and queue for sync
    console.log('Saving inspection:', inspectionData);

    setTimeout(() => {
      setIsSaving(false);
      navigate(`/workorder/${workOrderId}`);
    }, 1000);
  };

  const handleOpenCompanyCam = () => {
    // Deep link to CompanyCam - opens search since this is a mock page
    // Real implementations use ccam://camera/{projectId}
    window.location.href = 'ccam://search';
  };

  const conditionOptions: { value: Condition; label: string; description: string; color: string }[] = [
    {
      value: 'good',
      label: 'Goed',
      description: 'Geen zichtbare schade of vlekken',
      color: 'border-green-500 bg-green-50',
    },
    {
      value: 'fair',
      label: 'Redelijk',
      description: 'Lichte slijtage of kleine vlekken',
      color: 'border-yellow-500 bg-yellow-50',
    },
    {
      value: 'poor',
      label: 'Slecht',
      description: 'Zichtbare schade of grote vlekken',
      color: 'border-red-500 bg-red-50',
    },
  ];

  return (
    <div className="space-y-lg">
      {/* Back button */}
      <Link
        to={`/workorder/${workOrderId}`}
        className="inline-flex items-center gap-xs text-kleda-primary hover:underline"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Terug naar werk order
      </Link>

      {/* Header */}
      <div>
        <h2 className="font-heading text-xl font-bold text-kleda-text-main">
          Voorinspectie
        </h2>
        <p className="text-kleda-text-muted">
          {item.Product__r?.Name || 'Item'} • {item.Name}
        </p>
      </div>

      {/* Condition */}
      <Card>
        <CardHeader>
          <CardTitle>Conditie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-sm">
            {conditionOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setCondition(option.value)}
                className={`w-full p-md rounded-kleda-sm border-2 text-left transition-colors ${
                  condition === option.value
                    ? option.color
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-kleda-text-main">{option.label}</p>
                    <p className="text-sm text-kleda-text-muted">{option.description}</p>
                  </div>
                  {condition === option.value && (
                    <svg className="w-5 h-5 text-kleda-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dimensions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Afmetingen</CardTitle>
            <button
              onClick={() => setIsRound(!isRound)}
              className="text-sm text-kleda-primary hover:underline"
            >
              {isRound ? 'Rechthoekig' : 'Rond'}
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {isRound ? (
            <div>
              <label className="kleda-label">Diameter (cm)</label>
              <input
                type="number"
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                placeholder="bijv. 200"
                className="kleda-input"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-md">
              <div>
                <label className="kleda-label">Lengte (cm)</label>
                <input
                  type="number"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  placeholder="bijv. 300"
                  className="kleda-input"
                />
              </div>
              <div>
                <label className="kleda-label">Breedte (cm)</label>
                <input
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="bijv. 200"
                  className="kleda-input"
                />
              </div>
            </div>
          )}

          {/* Calculated area */}
          {(isRound ? diameter : length && width) && (
            <div className="mt-md p-sm bg-kleda-bg-clean rounded-kleda-sm text-center">
              <span className="text-sm text-kleda-text-muted">Oppervlakte: </span>
              <span className="font-medium text-kleda-text-main">
                {isRound
                  ? (Math.PI * Math.pow(parseFloat(diameter || '0') / 200, 2)).toFixed(2)
                  : ((parseFloat(length || '0') * parseFloat(width || '0')) / 10000).toFixed(2)
                } m²
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photos */}
      <Card>
        <CardHeader>
          <CardTitle>Foto's</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-sm mb-md">
            {/* Photo placeholders */}
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspect-square bg-kleda-bg-clean rounded-kleda-sm flex items-center justify-center border-2 border-dashed border-gray-300"
              >
                <svg className="w-8 h-8 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            ))}
          </div>

          <Button variant="outline" fullWidth onClick={handleOpenCompanyCam}>
            <svg className="w-4 h-4 mr-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Open CompanyCam
          </Button>
          <p className="text-center text-xs text-kleda-text-muted mt-sm">
            Maak foto's met CompanyCam
          </p>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Opmerkingen</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Eventuele bijzonderheden..."
            rows={3}
            className="kleda-input resize-none"
          />
        </CardContent>
      </Card>

      {/* Save button */}
      <Card>
        <CardContent>
          <Button
            fullWidth
            size="lg"
            onClick={handleSave}
            isLoading={isSaving}
          >
            Inspectie opslaan
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
